<template>
	<div>
		<section>
			<div v-show="!this.$store.state.socket.isConnected">
				<el-alert :title="$t('chat.realTimeServer')" type="warning" show-icon />
			</div>
			<div class="flex-container" v-loading="loading">
				<!-- 侧边栏 -->
				<div class="flex-left" :style="{ width: leftWidth + 'px' }">
					<div class="search-keyword">
						<el-input
							prefix-icon="el-icon-search"
							ref="focuseSearchKeyword"
							:placeholder="$t('chat.enterSearchContent')"
							clearable
							size="mini"
							v-model="searchKeyword"
							@focus="onFocus"
						></el-input>
					</div>
					<div class="communication-search" :style="{ height: tabHeight + 'px', width: leftWidth + 'px' }" v-if="searchKeyword && isSearchFocused">
						<ConversationList
							:data="conversationListData"
							@row-click="handleConversationListClick"
							@more-customer-click="handleContactDetailClick($event)"
							:isHeader="true"
							v-show="conversationListData.length > 0"
						/>
						<GroupList :data.sync="groupsData" @row-click="handleGroupsRowClick" :isHeader="true" v-show="groupsData.length > 0" />
						<Contacts :data="contactsData" :isHeader="true" @row-click="handleContactsClick" v-show="contactsData.length > 0" />
					</div>

					<el-tabs type="border-card">
						<!-- 最近聊天 -->
						<el-tab-pane :label="$t('chat.recentChat')">
							<ConversationList
								ref="conversationList"
								:height="tabHeight"
								:data="conversations"
								@row-click="handleConversationListClick"
								@more-customer-click="handleContactDetailClick($event)"
							/>
						</el-tab-pane>
						<!-- 通讯录 -->
						<el-tab-pane :label="$t('chat.addressBook')">
							<ContactList :data="contacts" @node-click="handleContactsClick" :height="tabHeight" />
						</el-tab-pane>
						<!-- 群聊 -->
						<el-tab-pane :label="$t('chat.groupChat')">
							<GroupList :height="tabHeight" :data.sync="groups" @row-click="handleGroupsRowClick" />
						</el-tab-pane>
					</el-tabs>
				</div>

				<!-- 拖动分隔条 -->
				<div class="resize-handle" @mousedown="startResize"></div>

				<!-- 内容 -->
				<div class="flex-center" v-show="conversation">
					<!-- 标题 -->
					<ChatTitle :conversation="conversation" @contact-detail-click="handleContactDetailClick" @group-detail-click="handleGroupDetailClick" />
					<!-- 消息内容 -->
					<ChatMessages
						:height="messageHeight"
						:messages="messages"
						:conversation="conversation"
						@recall-message="handleRecallMessage"
						@resend-message="handleResendMessage"
						@handle-image-view="handleImageView"
						@handle-video-viewer="handleVideoViewer"
					/>
					<!-- 工具栏 -->
					<div class="toolbar">
						<div class="toolbar-item">
							<ChatEmojiPicker @select="handleEmojiSelect" />
						</div>

						<div class="toolbar-item">
							<input ref="fileInput" type="file" accept="image/*,.mp4" multiple style="display: none" @change="handleNativeFileChange" />
							<el-button size="mini" icon="el-icon-picture" @click="triggerFileSelect" />
						</div>
						<div class="toolbar-item">
							<ChatRecorder @done="handleRecorderDone" />
						</div>
						<div class="toolbar-item-right">
							<el-button type="default" size="mini" icon="el-icon-time" @click="handleHistoryClick">{{ $t("chat.chatRecord") }}</el-button>
						</div>
						<div class="toolbar-item-right">
							<el-select
								v-model="languageValue"
								@change="handleSelectLanguage"
								:placeholder="$t('chat.selectLanguage')"
								size="mini"
								style="margin-right: 5px"
								clearable
							>
								<el-option v-for="item in languageOptions" :key="item.value" :label="item.label" :value="item.value"> </el-option>
							</el-select>
						</div>
					</div>
					<!-- 文本输入框 -->
					<div class="input-wrap" v-on:paste="handleOnPaste">
						<el-input
							v-show="!languageValue"
							type="textarea"
							v-model="content"
							resize="none"
							:autosize="{ minRows: 3, maxRows: 15 }"
							maxlength="1000"
							:placeholder="getSendMethodPlaceholder()"
							@keydown.ctrl.enter.native="handleKeyboardSend('ctrl', $event)"
							@keydown.meta.enter.native="handleKeyboardSend('ctrl', $event)"
							@keydown.enter.exact.native="handleKeyboardSend('enter', $event)"
							@focus="handleTextareaFocus"
							@input="handleTextareaInput"
							ref="inputRef"
						>
						</el-input>
						<el-input
							v-show="languageValue"
							type="textarea"
							v-model="content"
							resize="none"
							:autosize="{ minRows: 3, maxRows: 15 }"
							maxlength="1000"
							:placeholder="getSendMethodPlaceholder()"
							@keydown.ctrl.enter.native="handleKeyboardSend('ctrl', $event)"
							@keydown.meta.enter.native="handleKeyboardSend('ctrl', $event)"
							@keydown.enter.exact.native="handleKeyboardSend('enter', $event)"
							@focus="handleTextareaFocus"
							@input="
								handleTranslatedContent();
								handleChangeTranslatedContent();
							"
							ref="inputTranslatedRef"
							style="width: 49%"
						>
						</el-input>
						<el-input
							v-show="languageValue"
							type="textarea"
							resize="none"
							:autosize="{ minRows: 3, maxRows: 15 }"
							v-model="translatedContent"
							maxlength="1000"
							style="width: 50%"
							:style="{ height: translatedContentHeight }"
							class="translate-content"
							ref="inputTranslatedContentRef"
							:placeholder="$t('chat.translateContent')"
						></el-input>
					</div>
					<!-- 发送按钮 -->
					<div class="send-button-wrap">
						<el-button type="primary" size="mini" icon="el-icon-message" @click="handleSend" :disabled="!content">
							{{ $t("chat.send") }}
						</el-button>

						<el-popover placement="top" trigger="click" v-model="sendConfigPopoverVisible" popper-class="send-method-popover">
							<div class="send-config-popover">
								<div class="config-section">
									<el-radio-group v-model="sendMethodConfig" class="config-radio-group" @change="saveSendConfig">
										<el-radio label="enter">
											<i class="el-icon-success check-icon" v-if="sendMethodConfig === Send_Method_TYPE.Enter"></i>
											<span class="radio-label">{{ $t("chat.enter") }}</span>
										</el-radio>
										<el-radio label="ctrl">
											<i class="el-icon-success check-icon" v-if="sendMethodConfig === Send_Method_TYPE.CtrlEnter"></i>
											<span class="radio-label">{{ this.isMac ? $t("chat.commandEnter") : $t("chat.ctrlEnter") }}</span>
										</el-radio>
									</el-radio-group>
								</div>
							</div>
							<i class="el-icon-setting setting-icon" slot="reference"></i>
						</el-popover>
					</div>
				</div>
				<!-- 推广列表 -->
				<div class="flex-right" v-show="conversation && conversation.object_type === 'personal'">
					<GeneralizationList
						:data="generalizations"
						:height="generalizationHeight"
						:detailDialogVisible.sync="generalizationDetailDialogVisible"
						:isHeader="true"
						@send-message="handleSendGeneralization"
						@search="syncGeneralizations"
						@openSendDialog="handleOpenSendDialog"
					/>
				</div>
			</div>
		</section>
		<!-- 发送推广消息的弹窗 -->
		<el-dialog :visible.sync="sendDialogVisible" width="320px" :show-close="false" :close-on-click-modal="false" :destroy-on-close="true">
			<div v-if="readyToSendGeneralization !== null" class="send-dialog-body">
				<div style="padding-bottom: 12px">{{ $t("generalization.isSendMessage") }}{{ readyToSendGeneralization.name1 }}?</div>
				<div>（SAP ID:{{ readyToSendGeneralization.customer_id }}）</div>
			</div>
			<span slot="footer" class="dialog-footer">
				<el-button @click="handleCloseSendDialog">
					{{ $t("common.cancel") }}
				</el-button>
				<el-button type="primary" @click="handleSendGeneralization(readyToSendGeneralization)">
					{{ $t("common.confirm") }}
				</el-button>
			</span>
		</el-dialog>

		<!-- 聊天历史 -->
		<ChatHistory ref="chatHistoryRef" :conversation="conversation" @handle-image-view="handleImageView" @handle-video-viewer="handleVideoViewer" />

		<!-- 图片粘贴器 -->
		<ChatPicturePaster ref="picturePasterRef" @onUpload="handlePicturePasterUpload" />

		<!-- 群聊信息 -->
		<DetailGroup ref="detailGroupRef" :conversation.sync="conversation" @handle-update-group-member-note="handleUpdateGroupMemberNote" />

		<!-- 私聊信息 -->
		<DetailContact ref="detailContactRef" :conversation.sync="conversation" @handle-conversations="handleConversations" />

		<!-- 图片查看 -->
		<ChatImageViewer ref="chatImageViewer" />

		<!-- 视频查看 -->
		<ChatVideoViewer ref="chatVideoViewer" />
	</div>
</template>

<script>
import chat from "./chat.js";
import { Send_Method_TYPE } from "./chat";
export default chat;
</script>

<style lang="less" scoped>
@import "./chat.less";
</style>

<style lang="less">
.send-method-popover {
	background-color: #2b2b2b !important;
	border-radius: 20px;
	.popper__arrow {
		transform: rotate(180deg);

		&::after {
			border-top-color: #444343 !important;
		}
	}
}
</style>
